import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase, TowJob, Profile, Claim, TowerService } from '../lib/supabase'
import { getAllRoles } from '../lib/roleUtils'
import {
  LayoutDashboard, Users, Truck, Wrench, Shield, DollarSign, Settings,
  CheckCircle, AlertTriangle, TrendingUp, BarChart3, Activity, LogOut,
  Building2, FileText, CreditCard, Star, Bell, Search, Filter, Plus, Menu, X, User, ChevronDown, BarChart, Clock, Phone, Download, Loader, Mail, MapPin, ArrowLeft, AlertCircle, Package
} from 'lucide-react'

type View = 'overview' | 'operators' | 'vendors' | 'insurers' | 'customers' | 'garages' | 'towers' | 'transactions' | 'settings' | 'jobs' | 'claims' | 'reports' | 'all-jobs' | 'tower-payments' | 'activity' | 'pending-registrations' | 'registration-details' | 'job-details'

interface AdminDashboardProps {
  initialTab?: View;
}

export default function AdminDashboard({ initialTab }: AdminDashboardProps = {}) {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [view, setView] = useState<View>(initialTab || 'overview')
  const [jobs, setJobs] = useState<TowJob[]>([])
  const [operators, setOperators] = useState<Profile[]>([])
  const [vendors, setVendors] = useState<Profile[]>([])
  const [insurers, setInsurers] = useState<Profile[]>([])
  const [customers, setCustomers] = useState<Profile[]>([])
  const [garages, setGarages] = useState<Profile[]>([])
  const [towers, setTowers] = useState<Profile[]>([])
  const [claims, setClaims] = useState<Claim[]>([])
  const [towerProfiles, setTowerProfiles] = useState<Map<string, Profile>>(new Map())
  const [garageProfiles, setGarageProfiles] = useState<Map<string, Profile>>(new Map())
  const [customerProfiles, setCustomerProfiles] = useState<Map<string, Profile>>(new Map())
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [jobSearchTerm, setJobSearchTerm] = useState('')
  const [jobStatusFilter, setJobStatusFilter] = useState('all')
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [selectedJob, setSelectedJob] = useState<TowJob | null>(null)
  const [towerPaymentSearch, setTowerPaymentSearch] = useState('')
  const [confirmReimbursement, setConfirmReimbursement] = useState<TowJob | null>(null)
  const [processingReimbursement, setProcessingReimbursement] = useState(false)
  const [reimbursementSuccess, setReimbursementSuccess] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [actionSuccess, setActionSuccess] = useState<{ type: 'approve' | 'reject' | null }>({ type: null })
  const [previousView, setPreviousView] = useState<View>('overview')
  const [reportStartDate, setReportStartDate] = useState('')
  const [reportEndDate, setReportEndDate] = useState('')
  const [reportSearchTerm, setReportSearchTerm] = useState('')
  const [selectedReportUser, setSelectedReportUser] = useState<Profile | null>(null)
  const [towerServices, setTowerServices] = useState<Map<string, TowerService>>(new Map())
  const [userRoles, setUserRoles] = useState<Map<string, string[]>>(new Map())
  const [towerSearch, setTowerSearch] = useState('')
  const [garageSearch, setGarageSearch] = useState('')
  const [vendorSearch, setVendorSearch] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [operatorSearch, setOperatorSearch] = useState('')
  const [insurerSearch, setInsurerSearch] = useState('')
  const filterDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false)
      }
    }

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFilterDropdown])

  useEffect(() => {
    if (!profile) return
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => {
      const jobsData = (data as TowJob[]) || []
      setJobs(jobsData)

      // Fetch customer, tower and garage profiles
      const customerIds = [...new Set(jobsData.map(j => j.customer_id).filter(Boolean))]
      const towerIds = [...new Set(jobsData.map(j => j.selected_tower_id || j.operator_id).filter(Boolean))]
      const garageIds = [...new Set(jobsData.map(j => j.destination_garage_id).filter(Boolean))]
      const serviceIds = [...new Set(jobsData.map(j => j.service_id).filter(Boolean))]

      if (customerIds.length > 0) {
        supabase.from('profiles').select('*').in('id', customerIds).then(({ data }) => {
          const customerMap = new Map((data as Profile[]).map(p => [p.id, p]))
          setCustomerProfiles(customerMap)
        })
      }

      if (towerIds.length > 0) {
        supabase.from('profiles').select('*').in('id', towerIds).then(({ data }) => {
          const towerMap = new Map((data as Profile[]).map(p => [p.id, p]))
          setTowerProfiles(towerMap)
        })
      }

      if (garageIds.length > 0) {
        supabase.from('profiles').select('*').in('id', garageIds).then(({ data }) => {
          const garageMap = new Map((data as Profile[]).map(p => [p.id, p]))
          setGarageProfiles(garageMap)
        })
      }

      if (serviceIds.length > 0) {
        supabase.from('tower_services').select('*').in('id', serviceIds).then(({ data }) => {
          const serviceMap = new Map((data as TowerService[]).map(s => [s.id, s]))
          setTowerServices(serviceMap)
        })
      }
    })

    // Fetch user_roles and profiles in parallel
    Promise.all([
      supabase.from('user_roles').select('*'),
      supabase.from('profiles').select('*'),
      supabase.from('claims').select('*').order('created_at', { ascending: false })
    ]).then(([rolesResult, profilesResult, claimsResult]) => {
      // Process user_roles
      const rolesMap = new Map<string, string[]>()
      if (rolesResult.data) {
        rolesResult.data.forEach((ur: any) => {
          if (!rolesMap.has(ur.user_id)) {
            rolesMap.set(ur.user_id, [])
          }
          rolesMap.get(ur.user_id)!.push(ur.role)
        })
      }
      setUserRoles(rolesMap)

      // Process profiles and categorize
      const profiles = (profilesResult.data as Profile[]) || []
      const operators: Profile[] = []
      const vendors: Profile[] = []
      const insurers: Profile[] = []
      const customers: Profile[] = []
      const garages: Profile[] = []
      const towers: Profile[] = []

      profiles.forEach(profile => {
        const rolesFromTable = rolesMap.get(profile.id) || []
        const roleFromProfile = profile.role ? [profile.role] : []
        const allRoles = [...new Set([...rolesFromTable, ...roleFromProfile])]

        // Add to all relevant categories based on combined roles
        if (allRoles.includes('operator')) operators.push(profile)
        if (allRoles.includes('vendor') || allRoles.includes('parts_vendor')) vendors.push(profile)
        if (allRoles.includes('insurer')) insurers.push(profile)
        if (allRoles.includes('customer')) customers.push(profile)
        if (allRoles.includes('garage')) garages.push(profile)
        if (allRoles.includes('tower')) towers.push(profile)
      })

      setOperators(operators)
      setVendors(vendors)
      setInsurers(insurers)
      setCustomers(customers)
      setGarages(garages)
      setTowers(towers)
      setClaims((claimsResult.data as Claim[]) || [])
    })
  }, [profile])

  const totalRevenue = jobs.reduce((sum, job) => sum + Number(job.total_amount), 0)
  const activeJobs = jobs.filter(j => ['pending', 'dispatched', 'en_route', 'arrived', 'towing'].includes(j.status))

  // Helper function to get all roles for a profile (checks both role field and user_roles)
  const getProfileRoles = (profile: Profile): string[] => {
    const rolesFromTable = userRoles.get(profile.id) || []
    const roleFromProfile = profile.role ? [profile.role] : []

    // Combine roles from both sources and deduplicate
    const allRoles = [...new Set([...rolesFromTable, ...roleFromProfile])]
    return allRoles
  }

  // Helper to get unique pending registrations (avoid duplicates for multi-role users)
  const getUniquePendingRegistrations = (): Profile[] => {
    const allPending = [...operators, ...vendors, ...towers, ...garages]
      .filter(p => p.verification_status === 'pending' && p.onboarding_completed)
    const uniqueMap = new Map<string, Profile>()
    allPending.forEach(p => uniqueMap.set(p.id, p))
    return Array.from(uniqueMap.values())
  }

  // Filter jobs by date range for reports
  const filteredJobs = jobs.filter(job => {
    if (!reportStartDate && !reportEndDate) return true
    const jobDate = new Date(job.created_at)
    if (reportStartDate && jobDate < new Date(reportStartDate)) return false
    if (reportEndDate && jobDate > new Date(reportEndDate + 'T23:59:59')) return false
    return true
  })

  // Filter jobs by selected user for reports
  const userFilteredJobs = selectedReportUser ? filteredJobs.filter(job => {
    if (selectedReportUser.role === 'customer' && job.customer_id === selectedReportUser.id) return true
    if (selectedReportUser.role === 'tower' && (job.selected_tower_id === selectedReportUser.id || job.operator_id === selectedReportUser.id)) return true
    if (selectedReportUser.role === 'garage' && job.destination_garage_id === selectedReportUser.id) return true
    if (selectedReportUser.role === 'parts_vendor' && job.service_type === 'parts' && job.vendor_id === selectedReportUser.id) return true
    return false
  }) : filteredJobs

  // Search users for reports
  const searchResults = reportSearchTerm ? [...customers, ...towers, ...garages, ...vendors.filter(v => v.role === 'parts_vendor')].filter(user => {
    const searchLower = reportSearchTerm.toLowerCase()
    return (user.full_name || '').toLowerCase().includes(searchLower) ||
           (user.business_name || '').toLowerCase().includes(searchLower) ||
           (user.email || '').toLowerCase().includes(searchLower) ||
           (user.phone || '').toLowerCase().includes(searchLower)
  }).slice(0, 10) : []

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('sidebar-open')
    } else {
      document.body.classList.remove('sidebar-open')
    }
    return () => {
      document.body.classList.remove('sidebar-open')
    }
  }, [mobileMenuOpen])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Overview</div>
            <div className={`sidebar-item ${view === 'overview' ? 'active' : ''}`} onClick={() => setView('overview')}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Users</div>
            <div className={`sidebar-item ${view === 'pending-registrations' ? 'active' : ''}`} onClick={() => setView('pending-registrations')}>
              <User size={20} />
              <span>Pending Registrations</span>
              <span style={{ marginLeft: 'auto', background: 'var(--amber)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {getUniquePendingRegistrations().length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'operators' ? 'active' : ''}`} onClick={() => setView('operators')}>
              <Truck size={20} />
              <span>Operators</span>
              <span style={{ marginLeft: 'auto', background: 'var(--blue)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {operators.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'vendors' ? 'active' : ''}`} onClick={() => setView('vendors')}>
              <Wrench size={20} />
              <span>Vendors</span>
              <span style={{ marginLeft: 'auto', background: 'var(--green)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {vendors.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'insurers' ? 'active' : ''}`} onClick={() => setView('insurers')}>
              <Shield size={20} />
              <span>Insurers</span>
              <span style={{ marginLeft: 'auto', background: 'var(--purple)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {insurers.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'customers' ? 'active' : ''}`} onClick={() => setView('customers')}>
              <User size={20} />
              <span>Customers</span>
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {customers.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'garages' ? 'active' : ''}`} onClick={() => setView('garages')}>
              <Building2 size={20} />
              <span>Garages</span>
              <span style={{ marginLeft: 'auto', background: 'var(--amber)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {garages.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'towers' ? 'active' : ''}`} onClick={() => setView('towers')}>
              <Truck size={20} />
              <span>Towers</span>
              <span style={{ marginLeft: 'auto', background: 'var(--blue)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {towers.length}
              </span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Operations</div>
            <div className={`sidebar-item ${view === 'all-jobs' ? 'active' : ''}`} onClick={() => setView('all-jobs')}>
              <Truck size={20} />
              <span>All Jobs</span>
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {jobs.length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'activity' ? 'active' : ''}`} onClick={() => navigate('/admin/activity')}>
              <Activity size={20} />
              <span>Activity Monitor</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Financial</div>
            <div className={`sidebar-item ${view === 'tower-payments' ? 'active' : ''}`} onClick={() => setView('tower-payments')}>
              <DollarSign size={20} />
              <span>Tower Payments</span>
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {jobs.filter(j => j.service_type === 'towing' && j.status === 'completed').length}
              </span>
            </div>
            <div className={`sidebar-item ${view === 'transactions' ? 'active' : ''}`} onClick={() => setView('transactions')}>
              <CreditCard size={20} />
              <span>All Transactions</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Reports</div>
            <div className={`sidebar-item ${view === 'reports' ? 'active' : ''}`} onClick={() => setView('reports')}>
              <BarChart size={20} />
              <span>Reports</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}>
              <Settings size={20} />
              <span>Settings</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-bottom-fixed">
          <div className="sidebar-footer">
            <div className="name">{profile?.full_name || 'Admin'}</div>
            <div className="role">{profile?.role || 'admin'}</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
        />
      )}

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)'
              }}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1>
                {view === 'overview' && 'Admin Dashboard'}
                {view === 'pending-registrations' && 'Pending Registrations'}
                {view === 'operators' && 'Operators Management'}
                {view === 'vendors' && 'Vendors Management'}
                {view === 'insurers' && 'Insurers Management'}
                {view === 'customers' && 'Customers Management'}
                {view === 'garages' && 'Garages Management'}
                {view === 'towers' && 'Towers Management'}
                {view === 'all-jobs' && 'All Jobs'}
                {view === 'tower-payments' && 'Tower Payments'}
                {view === 'transactions' && 'All Transactions'}
                {view === 'reports' && 'Reports'}
                {view === 'settings' && 'Settings'}
              </h1>
              <p className="subtitle">
                Platform Administration
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span>{profile?.full_name?.split(' ')[0] || 'Admin'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setView('settings'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    onClick={() => { signOut(); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {view === 'overview' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Truck size={24} />
                  </div>
                  <div className="stat-label">Total Jobs</div>
                  <div className="stat-value">{jobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    All service requests
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{jobs.filter(j => j.status === 'payment_completed').length}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Successfully completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Revenue</div>
                  <div className="stat-value">KES {totalRevenue.toLocaleString()}</div>
                  <div className="stat-trend positive">
                    <BarChart3 size={14} />
                    Platform revenue
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
                    <Users size={24} />
                  </div>
                  <div className="stat-label">Total Users</div>
                  <div className="stat-value">{operators.length + vendors.length + insurers.length}</div>
                  <div className="stat-trend">
                    <Building2 size={14} />
                    Registered users
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="card">
                  <h2 className="card-title">Pending Verifications</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'var(--amber-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--amber)',
                      }}>
                        <Truck size={20} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Operators</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                          {operators.filter(o => o.verification_status === 'pending').length} pending
                        </div>
                      </div>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => setView('pending-registrations')}
                      >
                        Review
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'var(--green-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--green)',
                      }}>
                        <Wrench size={20} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Vendors</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                          {vendors.filter(v => v.verification_status === 'pending').length} pending
                        </div>
                      </div>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => setView('pending-registrations')}
                      >
                        Review
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h2 className="card-title">Active Jobs</h2>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.5rem' }}>
                    {activeJobs.length}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                    Jobs currently in progress
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Recent Activity</h2>
                {jobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No recent activity</p>
                  </div>
                ) : (
                <>
                  <div className="data-table-wrapper">
                  <table className="data-table">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Customer</th>
                          <th>Service Type</th>
                          <th>Handler</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                      </thead>
                    <tbody>
                      {jobs.slice(0, 10).map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>
                            <div style={{ fontWeight: 500 }}>
                              {(() => {
                                const customer = customerProfiles.get(job.customer_id)
                                return customer?.full_name || job.customer_name || job.customer_phone || 'N/A'
                              })()}
                            </div>
                          </td>
                          <td>{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</td>
                          <td>
                            {(() => {
                              const towerId = job.selected_tower_id || job.operator_id
                              const tower = towerId ? towerProfiles.get(towerId) : null
                              return (
                                <div style={{ lineHeight: '1.2' }}>
                                  <div style={{ fontWeight: 500, fontSize: '0.75rem' }}>{tower?.full_name || towerId || 'N/A'}</div>
                                  <div style={{ fontSize: '0.65rem', color: 'var(--muted)' }}>{tower?.role || 'N/A'}</div>
                                </div>
                              )
                            })()}
                          </td>
                          <td>
                            <span className={`badge ${
                              job.status === 'payment_completed' ? 'badge-green' :
                              job.status === 'cancelled' ? 'badge-red' :
                              'badge-blue'
                            }`}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              className="btn btn-sm"
                              onClick={() => {
                                setPreviousView('overview')
                                setSelectedJob(job)
                                setView('job-details')
                              }}
                              style={{
                                padding: '0.2rem 0.5rem',
                                background: 'var(--red)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontSize: '0.65rem',
                                fontWeight: 600
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card version */}
                <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                  {jobs.slice(0, 10).map(job => (
                    <div key={job.id} className="data-table-card">
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Reference</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>{job.reference}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Customer</span>
                        <span className="data-table-card-value">{(() => {
                          const customer = customerProfiles.get(job.customer_id)
                          return customer?.full_name || job.customer_name || job.customer_phone || 'N/A'
                        })()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Service Type</span>
                        <span className="data-table-card-value">{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Handler</span>
                        <span className="data-table-card-value">{(() => {
                          const towerId = job.selected_tower_id || job.operator_id
                          const tower = towerId ? towerProfiles.get(towerId) : null
                          return (
                            <div style={{ lineHeight: '1.1' }}>
                              <div>{tower?.full_name || towerId || 'N/A'}</div>
                              <div style={{ fontSize: '0.6rem', color: 'var(--muted)' }}>{tower?.role || 'N/A'}</div>
                            </div>
                          )
                        })()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Status</span>
                        <span className="data-table-card-value">
                          <span className={`badge ${
                            job.status === 'payment_completed' ? 'badge-green' :
                            job.status === 'cancelled' ? 'badge-red' :
                            'badge-blue'
                          }`} style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Date</span>
                        <span className="data-table-card-value">{new Date(job.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Amount</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>KES {Number(job.total_amount).toLocaleString()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Action</span>
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            setPreviousView('overview')
                            setSelectedJob(job)
                            setView('job-details')
                          }}
                          style={{
                            padding: '0.2rem 0.5rem',
                            background: 'var(--red)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: '0.65rem',
                            fontWeight: 600
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
                )}
              </div>
            </>
          )}

          {view === 'all-jobs' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>All Jobs</h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'var(--red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      <Filter size={16} />
                      Filter
                      <ChevronDown size={14} style={{ marginLeft: '0.25rem' }} />
                    </button>
                    {showFilterDropdown && (
                      <div ref={filterDropdownRef} style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.5rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '0.5rem',
                        minWidth: '150px',
                        zIndex: 10,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                        <div
                          onClick={() => { setJobStatusFilter('all'); setShowFilterDropdown(false) }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            background: jobStatusFilter === 'all' ? 'var(--bg)' : 'transparent'
                          }}
                        >
                          All Status
                        </div>
                        <div
                          onClick={() => { setJobStatusFilter('pending'); setShowFilterDropdown(false) }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            background: jobStatusFilter === 'pending' ? 'var(--bg)' : 'transparent'
                          }}
                        >
                          Pending
                        </div>
                        <div
                          onClick={() => { setJobStatusFilter('completed'); setShowFilterDropdown(false) }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            background: jobStatusFilter === 'completed' ? 'var(--bg)' : 'transparent'
                          }}
                        >
                          Completed
                        </div>
                        <div
                          onClick={() => { setJobStatusFilter('dispatched'); setShowFilterDropdown(false) }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            background: jobStatusFilter === 'dispatched' ? 'var(--bg)' : 'transparent'
                          }}
                        >
                          Dispatched
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={jobSearchTerm}
                      onChange={(e) => setJobSearchTerm(e.target.value)}
                      style={{
                        padding: '0.5rem 1rem 0.5rem 2.5rem',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem',
                        minWidth: '200px'
                      }}
                    />
                  </div>
                </div>
              </div>
              {jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No jobs found</p>
                </div>
              ) : (
              <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Customer</th>
                      <th>Service Type</th>
                      <th>Handler</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs
                      .filter(job => {
                        const customer = customerProfiles.get(job.customer_id)
                        const customerInfo = `${customer?.full_name || job.customer_name || ''} ${customer?.phone || job.customer_phone || ''}`.toLowerCase()
                        const matchesSearch = !jobSearchTerm ||
                          job.reference.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                          job.service_type.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                          customerInfo.includes(jobSearchTerm.toLowerCase())
                        const matchesFilter = jobStatusFilter === 'all' || job.status === jobStatusFilter
                        return matchesSearch && matchesFilter
                      })
                      .map(job => (
                      <tr key={job.id}>
                        <td style={{ fontWeight: 600 }}>{job.reference}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {(() => {
                              const customer = customerProfiles.get(job.customer_id)
                              return customer?.full_name || job.customer_name || job.customer_phone || 'N/A'
                            })()}
                          </div>
                        </td>
                        <td>{job.service_type.replace('_', ' ')}</td>
                        <td>
                          {(() => {
                            const towerId = job.selected_tower_id || job.operator_id
                            const tower = towerId ? towerProfiles.get(towerId) : null
                            return (
                              <div style={{ lineHeight: '1.2' }}>
                                <div style={{ fontWeight: 500, fontSize: '0.75rem' }}>{tower?.full_name || towerId || 'N/A'}</div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--muted)' }}>{tower?.role || 'N/A'}</div>
                              </div>
                            )
                          })()}
                        </td>
                        <td>
                          <span className={`badge ${
                            job.status === 'payment_completed' ? 'badge-green' :
                            job.status === 'cancelled' ? 'badge-red' :
                            job.status === 'completed' ? 'badge-green' :
                            'badge-blue'
                          }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{new Date(job.created_at).toLocaleDateString()}</td>
                        <td style={{ fontWeight: 600 }}>
                          KES {Number(job.total_amount).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => {
                              setPreviousView('all-jobs')
                              setSelectedJob(job)
                              setView('job-details')
                            }}
                            style={{
                              padding: '0.25rem 0.75rem',
                              background: 'var(--red)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                {/* Mobile card version */}
                <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                  {jobs
                    .filter(job => {
                      const customer = customerProfiles.get(job.customer_id)
                      const customerInfo = `${customer?.full_name || job.customer_name || ''} ${customer?.phone || job.customer_phone || ''}`.toLowerCase()
                      const matchesSearch = !jobSearchTerm ||
                        job.reference.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                        job.service_type.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                        customerInfo.includes(jobSearchTerm.toLowerCase())
                      const matchesFilter = jobStatusFilter === 'all' || job.status === jobStatusFilter
                      return matchesSearch && matchesFilter
                    })
                    .map(job => (
                    <div key={job.id} className="data-table-card">
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Reference</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>{job.reference}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Customer</span>
                        <span className="data-table-card-value">{(() => {
                          const customer = customerProfiles.get(job.customer_id)
                          return customer?.full_name || job.customer_name || job.customer_phone || 'N/A'
                        })()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Service Type</span>
                        <span className="data-table-card-value">{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Handler</span>
                        <span className="data-table-card-value">{(() => {
                          const towerId = job.selected_tower_id || job.operator_id
                          const tower = towerId ? towerProfiles.get(towerId) : null
                          return (
                            <div style={{ lineHeight: '1.1' }}>
                              <div>{tower?.full_name || towerId || 'N/A'}</div>
                              <div style={{ fontSize: '0.6rem', color: 'var(--muted)' }}>{tower?.role || 'N/A'}</div>
                            </div>
                          )
                        })()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Status</span>
                        <span className="data-table-card-value">
                          <span className={`badge ${
                            job.status === 'payment_completed' ? 'badge-green' :
                            job.status === 'cancelled' ? 'badge-red' :
                            job.status === 'completed' ? 'badge-green' :
                            'badge-blue'
                          }`} style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Date</span>
                        <span className="data-table-card-value">{new Date(job.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Amount</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>KES {Number(job.total_amount).toLocaleString()}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Action</span>
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            setPreviousView('all-jobs')
                            setSelectedJob(job)
                            setView('job-details')
                          }}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: 'var(--red)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
              )}
            </div>
          )}

          {/* Job Details View */}
          {view === 'job-details' && selectedJob && (
            <div className="card">
              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  onClick={() => {
                    if (previousView) {
                      setView(previousView)
                    } else {
                      setView('overview')
                    }
                    setSelectedJob(null)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--blue)',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Job Details</h2>
                <p style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{selectedJob.reference}</p>
              </div>

              {/* Content */}
              <div style={{ overflow: 'auto' }}>
                {/* Customer Information */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Customer Information</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <User size={14} style={{ color: 'var(--blue)' }} />
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)' }}>Name</div>
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)' }}>{(() => {
                        const customer = customerProfiles.get(selectedJob.customer_id)
                        return customer?.full_name || selectedJob.customer_name || 'N/A'
                      })()}</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <Phone size={14} style={{ color: 'var(--green)' }} />
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)' }}>Phone</div>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{(() => {
                        const customer = customerProfiles.get(selectedJob.customer_id)
                        return customer?.phone || selectedJob.customer_phone || 'N/A'
                      })()}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <Truck size={16} style={{ color: 'var(--blue)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Service Type</div>
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>{selectedJob.service_id ? towerServices.get(selectedJob.service_id)?.service_name || selectedJob.service_type.replace('_', ' ') : selectedJob.service_type.replace('_', ' ')}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <User size={16} style={{ color: 'var(--purple)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Handler</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{(() => {
                        const towerId = selectedJob.selected_tower_id || selectedJob.operator_id
                        const tower = towerId ? towerProfiles.get(towerId) : null
                        return tower?.full_name || towerId || 'N/A'
                      })()}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--muted)' }}>{(() => {
                        const towerId = selectedJob.selected_tower_id || selectedJob.operator_id
                        const tower = towerId ? towerProfiles.get(towerId) : null
                        return tower?.role || 'N/A'
                      })()}</div>
                    </div>
                  </div>
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <CheckCircle size={16} style={{ color: 'var(--green)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                    </div>
                    <span className={`badge ${
                      selectedJob.status === 'payment_completed' ? 'badge-green' :
                      selectedJob.status === 'cancelled' ? 'badge-red' :
                      selectedJob.status === 'completed' ? 'badge-green' :
                      'badge-blue'
                    }`} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                      {selectedJob.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <DollarSign size={16} style={{ color: 'var(--red)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</div>
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)' }}>KES {Number(selectedJob.total_amount).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <Clock size={16} style={{ color: 'var(--purple)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{new Date(selectedJob.created_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {selectedJob.completed_at && (
                  <div style={{ padding: '0.75rem', background: '#dcfce7', borderRadius: 'var(--radius)', border: '2px solid #86efac', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <CheckCircle size={14} style={{ color: '#166534' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Completed</div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#166534', fontWeight: 500 }}>{new Date(selectedJob.completed_at).toLocaleString()}</div>
                  </div>
                )}

                {selectedJob.diagnosis_notes && (
                  <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FileText size={14} style={{ color: 'var(--muted)' }} />
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text)', lineHeight: '1.5' }}>{selectedJob.diagnosis_notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Job Details Modal - Only show when not in job-details view */}
          {selectedJob && view !== 'job-details' && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-lg)',
                padding: '0',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}>
                {/* Header */}
                <div style={{
                  padding: '1.5rem 2rem',
                  borderBottom: '2px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--bg)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'var(--red)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 700
                    }}>
                      {selectedJob.reference.substring(0, 2)}
                    </div>
                    <div>
                      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Job Details</h2>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{selectedJob.reference}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedJob(null)
                      if (previousView) {
                        setView(previousView)
                      }
                    }}
                    style={{
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Content */}
                <div style={{ padding: '2rem', overflow: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
                  {/* Customer Information */}
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Customer Information</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <User size={16} style={{ color: 'var(--blue)' }} />
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Name</div>
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>{(() => {
                        const customer = customerProfiles.get(selectedJob.customer_id)
                        return customer?.full_name || selectedJob.customer_name || 'N/A'
                      })()}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Phone size={16} style={{ color: 'var(--green)' }} />
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Phone</div>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{(() => {
                        const customer = customerProfiles.get(selectedJob.customer_id)
                        return customer?.phone || selectedJob.customer_phone || 'N/A'
                      })()}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
                    <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <Truck size={18} style={{ color: 'var(--blue)' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Service Type</div>
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>{selectedJob.service_id ? towerServices.get(selectedJob.service_id)?.service_name || selectedJob.service_type.replace('_', ' ') : selectedJob.service_type.replace('_', ' ')}</div>
                    </div>
                    <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <CheckCircle size={18} style={{ color: 'var(--green)' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      </div>
                      <span className={`badge ${
                        selectedJob.status === 'payment_completed' ? 'badge-green' :
                        selectedJob.status === 'cancelled' ? 'badge-red' :
                        selectedJob.status === 'completed' ? 'badge-green' :
                        'badge-blue'
                      }`} style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}>
                        {selectedJob.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <DollarSign size={18} style={{ color: 'var(--red)' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</div>
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>KES {Number(selectedJob.total_amount).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <Clock size={18} style={{ color: 'var(--purple)' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</div>
                      </div>
                      <div style={{ fontSize: '1rem', color: 'var(--text)' }}>{new Date(selectedJob.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>

                  {selectedJob.completed_at && (
                    <div style={{ padding: '1.25rem', background: '#dcfce7', borderRadius: 'var(--radius)', border: '2px solid #86efac', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <CheckCircle size={18} style={{ color: '#166534' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Completed</div>
                      </div>
                      <div style={{ fontSize: '1rem', color: '#166534', fontWeight: 500 }}>{new Date(selectedJob.completed_at).toLocaleString()}</div>
                    </div>
                  )}

                  {selectedJob.diagnosis_notes && (
                    <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <FileText size={18} style={{ color: 'var(--muted)' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
                      </div>
                      <div style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: '1.6' }}>{selectedJob.diagnosis_notes}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reimbursement Confirmation Modal */}
          {confirmReimbursement && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius)',
                maxWidth: '400px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                    {reimbursementSuccess ? 'Reimbursement Successful' : 'Confirm Reimbursement'}
                  </h3>
                  {!processingReimbursement && (
                    <button
                      onClick={() => {
                        setConfirmReimbursement(null)
                        setReimbursementSuccess(false)
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        color: 'var(--muted)'
                      }}
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
                <div style={{ padding: '1.5rem' }}>
                  {reimbursementSuccess ? (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: 'var(--green)', marginBottom: '1rem' }}>
                        <CheckCircle size={64} style={{ margin: '0 auto 1rem' }} />
                      </div>
                      <p style={{ color: 'var(--text)', marginBottom: '1rem' }}>
                        The reimbursement has been successfully processed.
                      </p>
                      <button
                        onClick={() => {
                          setConfirmReimbursement(null)
                          setReimbursementSuccess(false)
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'var(--green)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: 'white',
                          width: '100%'
                        }}
                      >
                        Done
                      </button>
                    </div>
                  ) : processingReimbursement ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <Loader size={48} style={{ margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                      </div>
                      <p style={{ color: 'var(--text)' }}>Processing reimbursement...</p>
                    </div>
                  ) : (
                    <>
                      <p style={{ color: 'var(--text)', marginBottom: '1rem' }}>
                        Are you sure you want to mark this payment as reimbursed?
                      </p>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Reference</div>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{confirmReimbursement.reference}</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem', marginTop: '0.5rem' }}>Amount</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1.125rem' }}>
                          KES {Number(confirmReimbursement.total_amount).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setConfirmReimbursement(null)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'white',
                            border: '2px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: 'var(--text)'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            setProcessingReimbursement(true)
                            // Update database
                            supabase
                              .from('tow_jobs')
                              .update({
                                reimbursed_at: new Date().toISOString(),
                                reimbursed_by: profile?.id
                              })
                              .eq('id', confirmReimbursement.id)
                              .then(({ error }) => {
                                setProcessingReimbursement(false)
                                if (error) {
                                  alert('Failed to process reimbursement: ' + error.message)
                                } else {
                                  setReimbursementSuccess(true)
                                  // Reload jobs to get updated data
                                  supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => {
                                    setJobs((data as TowJob[]) || [])
                                  })
                                }
                              })
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--green)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: 'white'
                          }}
                        >
                          Confirm Reimbursement
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {view === 'operators' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Operators Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search operators..."
                    value={operatorSearch}
                    onChange={(e) => setOperatorSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {operators.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No operators registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Truck Type</th>
                      <th>Plate</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operators
                      .filter(operator => {
                        if (!operatorSearch) return true
                        const searchLower = operatorSearch.toLowerCase()
                        return (operator.full_name || '').toLowerCase().includes(searchLower) ||
                               (operator.email || '').toLowerCase().includes(searchLower) ||
                               (operator.phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(operator => (
                      <tr key={operator.id}>
                        <td style={{ fontWeight: 600 }}>{operator.full_name}</td>
                        <td>{operator.business_phone || operator.phone}</td>
                        <td>{operator.truck_type}</td>
                        <td>{operator.truck_plate}</td>
                        <td>
                          <span className={`badge ${operator.verification_status === 'verified' ? 'badge-green' : operator.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                            {operator.verification_status}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('operators')
                            setSelectedProfile(operator)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {operators.map(operator => (
                      <div key={operator.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{operator.full_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Phone</span>
                          <span className="data-table-card-value">{operator.business_phone || operator.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Truck Type</span>
                          <span className="data-table-card-value">{operator.truck_type}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Plate</span>
                          <span className="data-table-card-value">{operator.truck_plate}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className={`badge ${operator.verification_status === 'verified' ? 'badge-green' : operator.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                              {operator.verification_status}
                            </span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('operators')
                            setSelectedProfile(operator)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'vendors' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Vendors Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {vendors.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Wrench size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No vendors registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors
                      .filter(vendor => {
                        if (!vendorSearch) return true
                        const searchLower = vendorSearch.toLowerCase()
                        return (vendor.business_name || '').toLowerCase().includes(searchLower) ||
                               (vendor.full_name || '').toLowerCase().includes(searchLower) ||
                               (vendor.email || '').toLowerCase().includes(searchLower) ||
                               (vendor.phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(vendor => (
                      <tr key={vendor.id}>
                        <td style={{ fontWeight: 600 }}>{vendor.business_name}</td>
                        <td>{vendor.business_phone || vendor.phone}</td>
                        <td>{vendor.business_address || 'N/A'}</td>
                        <td>
                          <span className={`badge ${vendor.verification_status === 'verified' ? 'badge-green' : vendor.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                            {vendor.verification_status}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('vendors')
                            setSelectedProfile(vendor)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {vendors.map(vendor => (
                      <div key={vendor.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Business Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{vendor.business_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Phone</span>
                          <span className="data-table-card-value">{vendor.business_phone || vendor.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Location</span>
                          <span className="data-table-card-value">{vendor.business_address || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className={`badge ${vendor.verification_status === 'verified' ? 'badge-green' : vendor.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                              {vendor.verification_status}
                            </span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('vendors')
                            setSelectedProfile(vendor)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'insurers' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Insurers Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search insurers..."
                    value={insurerSearch}
                    onChange={(e) => setInsurerSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {insurers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Shield size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No insurers registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Contact</th>
                      <th>Registered</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insurers
                      .filter(insurer => {
                        if (!insurerSearch) return true
                        const searchLower = insurerSearch.toLowerCase()
                        return (insurer.insurer_name || '').toLowerCase().includes(searchLower) ||
                               (insurer.full_name || '').toLowerCase().includes(searchLower) ||
                               (insurer.email || '').toLowerCase().includes(searchLower) ||
                               (insurer.phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(insurer => (
                      <tr key={insurer.id}>
                        <td style={{ fontWeight: 600 }}>{insurer.insurer_name}</td>
                        <td>{insurer.phone}</td>
                        <td>{insurer.insurer_since ? new Date(insurer.insurer_since).getFullYear() : 'N/A'}</td>
                        <td>
                          <span className="badge badge-green">Active</span>
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('insurers')
                            setSelectedProfile(insurer)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {insurers.map(insurer => (
                      <div key={insurer.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Company Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{insurer.insurer_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Contact</span>
                          <span className="data-table-card-value">{insurer.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Registered</span>
                          <span className="data-table-card-value">{insurer.insurer_since ? new Date(insurer.insurer_since).getFullYear() : 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className="badge badge-green">Active</span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('insurers')
                            setSelectedProfile(insurer)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'customers' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Customers Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {customers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <User size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No customers registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Registered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers
                      .filter(customer => {
                        if (!customerSearch) return true
                        const searchLower = customerSearch.toLowerCase()
                        return (customer.full_name || '').toLowerCase().includes(searchLower) ||
                               (customer.email || '').toLowerCase().includes(searchLower) ||
                               (customer.phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(customer => (
                      <tr key={customer.id}>
                        <td style={{ fontWeight: 600 }}>{customer.full_name}</td>
                        <td>{customer.phone}</td>
                        <td>{customer.email}</td>
                        <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('customers')
                            setSelectedProfile(customer)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {customers.map(customer => (
                      <div key={customer.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{customer.full_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Phone</span>
                          <span className="data-table-card-value">{customer.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Email</span>
                          <span className="data-table-card-value">{customer.email}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Registered</span>
                          <span className="data-table-card-value">{new Date(customer.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('customers')
                            setSelectedProfile(customer)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'garages' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Garages Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search garages..."
                    value={garageSearch}
                    onChange={(e) => setGarageSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {garages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Building2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No garages registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {garages
                      .filter(garage => {
                        if (!garageSearch) return true
                        const searchLower = garageSearch.toLowerCase()
                        return (garage.business_name || '').toLowerCase().includes(searchLower) ||
                               (garage.full_name || '').toLowerCase().includes(searchLower) ||
                               (garage.email || '').toLowerCase().includes(searchLower) ||
                               (garage.phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(garage => (
                      <tr key={garage.id}>
                        <td style={{ fontWeight: 600 }}>{garage.business_name}</td>
                        <td>{garage.phone}</td>
                        <td>{garage.business_address || 'N/A'}</td>
                        <td>
                          <span className={`badge ${garage.verification_status === 'verified' ? 'badge-green' : garage.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                            {garage.verification_status}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('garages')
                            setSelectedProfile(garage)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {garages.map(garage => (
                      <div key={garage.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Business Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{garage.business_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Phone</span>
                          <span className="data-table-card-value">{garage.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Location</span>
                          <span className="data-table-card-value">{garage.business_address || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className={`badge ${garage.verification_status === 'verified' ? 'badge-green' : garage.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                              {garage.verification_status}
                            </span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('garages')
                            setSelectedProfile(garage)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'towers' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Towers Management</h2>
                <div style={{ position: 'relative', width: '300px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search towers..."
                    value={towerSearch}
                    onChange={(e) => setTowerSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--bg)'
                    }}
                  />
                </div>
              </div>
              {towers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No towers registered</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Fleet</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {towers
                      .filter(tower => {
                        if (!towerSearch) return true
                        const searchLower = towerSearch.toLowerCase()
                        return (tower.full_name || '').toLowerCase().includes(searchLower) ||
                               (tower.email || '').toLowerCase().includes(searchLower) ||
                               (tower.phone || '').toLowerCase().includes(searchLower) ||
                               (tower.business_phone || '').toLowerCase().includes(searchLower)
                      })
                      .map(tower => (
                      <tr key={tower.id}>
                        <td style={{ fontWeight: 600 }}>{tower.full_name}</td>
                        <td>{tower.email}</td>
                        <td>{tower.business_phone || tower.phone}</td>
                        <td>
                          {tower.vehicle_photos && Array.isArray(tower.vehicle_photos) && tower.vehicle_photos.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {tower.vehicle_photos.map((truck: any, idx: number) => (
                                <div key={idx} style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                                  {truck.truck_type || 'N/A'} - {truck.truck_plate || 'N/A'}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                              {tower.truck_type || 'N/A'} - {tower.truck_plate || 'N/A'}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${tower.verification_status === 'verified' ? 'badge-green' : tower.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                            {tower.verification_status}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('towers')
                            setSelectedProfile(tower)
                            setView('registration-details')
                          }}>View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {towers.map(tower => (
                      <div key={tower.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Name</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{tower.full_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Email</span>
                          <span className="data-table-card-value">{tower.email}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Phone</span>
                          <span className="data-table-card-value">{tower.business_phone || tower.phone}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Fleet</span>
                          <span className="data-table-card-value">
                            {tower.vehicle_photos && Array.isArray(tower.vehicle_photos) && tower.vehicle_photos.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                {tower.vehicle_photos.map((truck: any, idx: number) => (
                                  <div key={idx} style={{ fontSize: '0.8rem' }}>
                                    {truck.truck_type || 'N/A'} - {truck.truck_plate || 'N/A'}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.8rem' }}>
                                {tower.truck_type || 'N/A'} - {tower.truck_plate || 'N/A'}
                              </div>
                            )}
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className={`badge ${tower.verification_status === 'verified' ? 'badge-green' : tower.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`}>
                              {tower.verification_status}
                            </span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setPreviousView('towers')
                            setSelectedProfile(tower)
                            setView('registration-details')
                          }}>View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'tower-payments' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Tower Payments</h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                    <input
                      type="text"
                      placeholder="Search payments..."
                      value={towerPaymentSearch}
                      onChange={(e) => setTowerPaymentSearch(e.target.value)}
                      style={{
                        padding: '0.5rem 1rem 0.5rem 2.5rem',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem',
                        minWidth: '200px'
                      }}
                    />
                  </div>
                  <button className="btn btn-outline">
                    <Filter size={16} />
                    Filter
                  </button>
                  <button className="btn btn-outline">
                    <Download size={16} />
                    Export
                  </button>
                </div>
              </div>

              {(() => {
                const filteredJobs = jobs
                  .filter(j => j.service_type === 'towing' && j.status === 'completed')
                  .filter(job => {
                    const towerId = job.selected_tower_id || job.operator_id
                    const garageId = job.destination_garage_id
                    const towerProfile = towerId ? towerProfiles.get(towerId) : null
                    const garageProfile = garageId ? garageProfiles.get(garageId) : null
                    const customer = customerProfiles.get(job.customer_id)

                    const searchLower = towerPaymentSearch.toLowerCase()
                    const matchesSearch = !towerPaymentSearch ||
                      job.reference.toLowerCase().includes(searchLower) ||
                      (customer?.full_name || job.customer_name || job.customer_phone || '').toLowerCase().includes(searchLower) ||
                      (towerProfile?.full_name || '').toLowerCase().includes(searchLower) ||
                      (garageProfile?.business_name || '').toLowerCase().includes(searchLower)

                    return matchesSearch
                  })

                if (filteredJobs.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                      <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <p>No tower payments yet</p>
                    </div>
                  )
                }

                return (
                  <>
                  <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Customer</th>
                        <th>Service</th>
                        <th>Tower</th>
                        <th>Garage</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Reimbursement</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobs.map(job => {
                        const towerId = job.selected_tower_id || job.operator_id
                        const garageId = job.destination_garage_id
                        const towerProfile = towerId ? towerProfiles.get(towerId) : null
                        const garageProfile = garageId ? garageProfiles.get(garageId) : null
                        const customer = customerProfiles.get(job.customer_id)

                        return (
                          <tr key={job.id}>
                            <td style={{ fontWeight: 600 }}>{job.reference}</td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{customer?.full_name || job.customer_name || job.customer_phone || 'N/A'}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{towerProfile?.full_name || towerId || 'N/A'}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{garageProfile?.business_name || garageId || 'N/A'}</div>
                            </td>
                            <td style={{ fontWeight: 600 }}>
                              KES {Number(job.total_amount).toLocaleString()}
                            </td>
                            <td>{new Date(job.created_at).toLocaleDateString()}</td>
                            <td>
                              {job.reimbursed_at ? (
                                <span className="badge badge-green">Reimbursed</span>
                              ) : (
                                <span className="badge badge-blue">Pending</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {!job.reimbursed_at && (
                                <button
                                  onClick={() => setConfirmReimbursement(job)}
                                  style={{
                                    padding: '0.2rem 0.5rem',
                                    background: 'var(--green)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    fontSize: '0.65rem',
                                    fontWeight: 600
                                  }}
                                >
                                  Process
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card version */}
                <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                  {filteredJobs.map(job => {
                    const towerId = job.selected_tower_id || job.operator_id
                    const garageId = job.destination_garage_id
                    const towerProfile = towerId ? towerProfiles.get(towerId) : null
                    const garageProfile = garageId ? garageProfiles.get(garageId) : null
                    const customer = customerProfiles.get(job.customer_id)

                    return (
                      <div key={job.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Reference</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{job.reference}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Customer</span>
                          <span className="data-table-card-value">{customer?.full_name || job.customer_name || job.customer_phone || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Service</span>
                          <span className="data-table-card-value">{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Tower</span>
                          <span className="data-table-card-value">{towerProfile?.full_name || towerId || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Garage</span>
                          <span className="data-table-card-value">{garageProfile?.business_name || garageId || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Amount</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>KES {Number(job.total_amount).toLocaleString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Date</span>
                          <span className="data-table-card-value">{new Date(job.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Reimbursement</span>
                          <span className="data-table-card-value">
                            {job.reimbursed_at ? (
                              <span className="badge badge-green">Reimbursed</span>
                            ) : (
                              <span className="badge badge-blue">Pending</span>
                            )}
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          {!job.reimbursed_at && (
                            <button
                              onClick={() => setConfirmReimbursement(job)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                background: 'var(--green)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            >
                              Process
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                </>
                )
              })()}
            </div>
          )}

          {view === 'transactions' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Platform Transactions</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-outline">
                    <Filter size={16} />
                    Filter
                  </button>
                  <button className="btn btn-outline">
                    <Download size={16} />
                    Export
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <DollarSign size={20} style={{ color: 'var(--green)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Total Revenue</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    KES {jobs.filter(j => j.status === 'completed').reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}
                  </div>
                </div>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <CheckCircle size={20} style={{ color: 'var(--blue)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Completed</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    {jobs.filter(j => j.status === 'completed').length}
                  </div>
                </div>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Clock size={20} style={{ color: 'var(--red)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Pending</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    {jobs.filter(j => j.status === 'pending').length}
                  </div>
                </div>
              </div>

              {jobs.filter(j => j.status === 'completed').length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <CreditCard size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Customer</th>
                      <th>Service Type</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.filter(j => j.status === 'completed').map(job => {
                      const customer = customerProfiles.get(job.customer_id)
                      return (
                      <tr key={job.id}>
                        <td style={{ fontWeight: 600 }}>{job.reference}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{customer?.full_name || job.customer_name || job.customer_phone || 'N/A'}</div>
                        </td>
                        <td>{job.service_type.replace('_', ' ')}</td>
                        <td style={{ fontWeight: 600 }}>
                          KES {Number(job.total_amount).toLocaleString()}
                        </td>
                        <td>{job.payment_method || 'Cash'}</td>
                        <td>{new Date(job.created_at).toLocaleDateString()}</td>
                        <td>
                          <span className="badge badge-green">Paid</span>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
                  </div>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {jobs.filter(j => j.status === 'completed').map(job => {
                      const customer = customerProfiles.get(job.customer_id)
                      return (
                      <div key={job.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Reference</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{job.reference}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Customer</span>
                          <span className="data-table-card-value">{customer?.full_name || job.customer_name || job.customer_phone || 'N/A'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Service Type</span>
                          <span className="data-table-card-value">{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Amount</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>KES {Number(job.total_amount).toLocaleString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Payment Method</span>
                          <span className="data-table-card-value">{job.payment_method || 'Cash'}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Date</span>
                          <span className="data-table-card-value">{new Date(job.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className="badge badge-green">Paid</span>
                          </span>
                        </div>
                      </div>
                    )})}
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'reports' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Platform Reports</h2>

              {/* User Search */}
              <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                  <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={reportSearchTerm}
                    onChange={(e) => setReportSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.6rem 1rem 0.6rem 2.5rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem'
                    }}
                  />
                  {selectedReportUser && (
                    <button
                      onClick={() => setSelectedReportUser(null)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'var(--border)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {searchResults.length > 0 && (
                  <div style={{ marginTop: '0.5rem', background: 'white', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', maxHeight: '200px', overflowY: 'auto' }}>
                    {searchResults.map(user => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedReportUser(user)
                          setReportSearchTerm('')
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                            {user.full_name || user.business_name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                            {user.email} • {user.role}
                          </div>
                        </div>
                        <span className={`badge ${
                          user.verification_status === 'verified' ? 'badge-green' :
                          user.verification_status === 'pending' ? 'badge-amber' :
                          'badge-blue'
                        }`} style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                          {user.verification_status || 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedReportUser && (
                <div style={{ marginBottom: '2rem', padding: '1rem', background: '#dcfce7', borderRadius: 'var(--radius)', border: '2px solid #86efac' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <User size={16} style={{ color: '#166534' }} />
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#166534' }}>
                      Showing reports for: {selectedReportUser.full_name || selectedReportUser.business_name || 'N/A'} ({selectedReportUser.role})
                    </div>
                  </div>
                </div>
              )}

              {/* Date Range Filter */}
              <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', display: 'block' }}>Start Date</label>
                    <input
                      type="date"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', display: 'block' }}>End Date</label>
                    <input
                      type="date"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setReportStartDate('')
                      setReportEndDate('')
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'var(--border)',
                      color: 'var(--text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Users size={20} style={{ color: 'var(--blue)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Total Users</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    {operators.length + vendors.length + insurers.length + customers.length + garages.length + towers.length}
                  </div>
                </div>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Truck size={20} style={{ color: 'var(--green)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Total Jobs</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    {userFilteredJobs.length}
                  </div>
                </div>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <CheckCircle size={20} style={{ color: 'var(--green)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Completed</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    {userFilteredJobs.filter(j => j.status === 'completed').length}
                  </div>
                </div>
                <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <DollarSign size={20} style={{ color: 'var(--red)' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)' }}>Revenue</div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                    KES {userFilteredJobs.filter(j => j.status === 'completed').reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Service Type Breakdown */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Service Type Breakdown</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                  {(() => {
                    // Build service counts with tower service names for towing
                    const serviceCounts = new Map<string, number>()
                    filteredJobs.forEach(job => {
                      if (job.service_id && towerServices.has(job.service_id)) {
                        const serviceName = towerServices.get(job.service_id)?.service_name || 'towing'
                        serviceCounts.set(serviceName, (serviceCounts.get(serviceName) || 0) + 1)
                      } else if (job.service_type === 'towing') {
                        serviceCounts.set('towing', (serviceCounts.get('towing') || 0) + 1)
                      } else {
                        serviceCounts.set(job.service_type, (serviceCounts.get(job.service_type) || 0) + 1)
                      }
                    })

                    return Array.from(serviceCounts.entries()).map(([serviceName, count]) => {
                      const percentage = filteredJobs.length > 0 ? (count / filteredJobs.length * 100).toFixed(1) : 0
                      return (
                        <div key={serviceName} style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize' }}>{serviceName.replace('_', ' ')}</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--red)' }}>{count}</span>
                          </div>
                          <div style={{ height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              background: 'var(--red)',
                              borderRadius: '4px',
                              width: `${percentage}%`,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{percentage}% of total</div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>

              {!selectedReportUser && (
                <>
                  {/* User Distribution */}
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>User Distribution</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <User size={16} style={{ color: 'var(--red)' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Customers</span>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>{customers.length}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <Truck size={16} style={{ color: 'var(--blue)' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Towers</span>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>{towers.length}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <Building2 size={16} style={{ color: 'var(--amber)' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Garages</span>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>{garages.length}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <Package size={16} style={{ color: 'var(--purple)' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Parts Vendors</span>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>{vendors.filter(v => v.role === 'parts_vendor').length}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <Shield size={16} style={{ color: 'var(--green)' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Insurers</span>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>{insurers.length}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Recent Activity */}
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>
                  {selectedReportUser ? 'User Activity' : 'Recent Activity'}
                </h3>
                <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)', overflow: 'hidden' }}>
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Service</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userFilteredJobs.slice(0, 5).map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>{job.service_id ? towerServices.get(job.service_id)?.service_name || job.service_type.replace('_', ' ') : job.service_type.replace('_', ' ')}</td>
                          <td>
                            <span className={`badge ${
                              job.status === 'completed' ? 'badge-green' :
                              job.status === 'pending' ? 'badge-blue' :
                              job.status === 'cancelled' ? 'badge-red' :
                              'badge-amber'
                            }`}>
                              {job.status}
                            </span>
                          </td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Date Range Filter */}
              <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', display: 'block' }}>Start Date</label>
                    <input
                      type="date"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', display: 'block' }}>End Date</label>
                    <input
                      type="date"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setReportStartDate('')
                      setReportEndDate('')
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'var(--border)',
                      color: 'var(--text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              </div>

              {/* Parts Sales Report */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Parts Sales Report</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Total Parts Sold</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {userFilteredJobs.filter(j => j.service_type === 'parts').length}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Parts Revenue</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      KES {userFilteredJobs.filter(j => j.service_type === 'parts').reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Active Parts Vendors</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {vendors.filter(v => v.role === 'parts_vendor').length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Towing Report */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Towing Services Report</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Total Towings</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {userFilteredJobs.filter(j => j.service_type === 'towing').length}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Towing Revenue</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      KES {userFilteredJobs.filter(j => j.service_type === 'towing').reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Active Towers</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {towers.length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Garage Services Report */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Garage Services Report</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Total Garage Services</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {userFilteredJobs.filter(j => j.destination_garage_id).length}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Garage Revenue</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      KES {userFilteredJobs.filter(j => j.destination_garage_id).reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem' }}>Active Garages</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text)' }}>
                      {garages.length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Per-User Performance */}
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>
                  {selectedReportUser ? 'User Performance' : 'Top Performing Users'}
                </h3>
                {selectedReportUser ? (
                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>
                      {selectedReportUser.full_name || selectedReportUser.business_name || 'N/A'} Performance
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>Total Jobs</span>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>{userFilteredJobs.length}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>Completed Jobs</span>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>{userFilteredJobs.filter(j => j.status === 'completed').length}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>Total Revenue</span>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>KES {userFilteredJobs.filter(j => j.status === 'completed').reduce((sum, j) => sum + Number(j.total_amount), 0).toLocaleString()}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                    {/* Top Towers */}
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Top Towers by Jobs</div>
                      {towers.slice(0, 5).map(tower => {
                        const towerJobs = userFilteredJobs.filter(j => j.selected_tower_id === tower.id || j.operator_id === tower.id)
                        return (
                          <div key={tower.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{tower.full_name || tower.business_name || 'N/A'}</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>{towerJobs.length} jobs</span>
                          </div>
                        )
                      })}
                    </div>

                    {/* Top Garages */}
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Top Garages by Services</div>
                      {garages.slice(0, 5).map(garage => {
                        const garageJobs = userFilteredJobs.filter(j => j.destination_garage_id === garage.id)
                        return (
                          <div key={garage.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{garage.business_name || 'N/A'}</span>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>{garageJobs.length} services</span>
                        </div>
                      )
                      })}
                    </div>

                    {/* Top Parts Vendors */}
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Top Parts Vendors by Sales</div>
                      {vendors.filter(v => v.role === 'parts_vendor').slice(0, 5).map(vendor => {
                        const vendorSales = userFilteredJobs.filter(j => j.service_type === 'parts' && j.vendor_id === vendor.id)
                        const totalRevenue = vendorSales.reduce((sum, j) => sum + Number(j.total_amount), 0)
                        return (
                          <div key={vendor.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{vendor.business_name || 'N/A'}</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--red)' }}>KES {totalRevenue.toLocaleString()}</span>
                        </div>
                      )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {view === 'pending-registrations' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>
                Pending Registrations
              </h2>
              <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>
                Review and approve business registrations (towers, garages, vendors)
              </p>

              {getUniquePendingRegistrations().length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <User size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No pending registrations</p>
                </div>
              ) : (
              <>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Name</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Role</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Email</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Phone</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Business</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Location</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Onboarding</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Status</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Submitted</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getUniquePendingRegistrations().map((profile) => (
                        <tr key={profile.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontWeight: 600 }}>{profile.full_name || 'N/A'}</div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {getProfileRoles(profile).map((role) => (
                                <span key={role} style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                  background: 'var(--bg)',
                                  color: 'var(--muted)'
                                }}>
                                  {role.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.9rem' }}>{profile.email}</td>
                          <td style={{ padding: '1rem', fontSize: '0.9rem' }}>{profile.business_phone || profile.phone || 'N/A'}</td>
                          <td style={{ padding: '1rem', fontSize: '0.9rem' }}>{profile.business_name || 'N/A'}</td>
                          <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                            {profile.location_city && profile.location_county ? `${profile.location_city}, ${profile.location_county}` : 'N/A'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            {profile.onboarding_completed ? (
                              <span style={{ color: 'var(--green)', fontWeight: 600 }}>Completed</span>
                            ) : (
                              <span style={{ color: 'var(--amber)', fontWeight: 600 }}>In Progress</span>
                            )}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              background: profile.verification_status === 'verified' ? '#dcfce7' :
                                         profile.verification_status === 'rejected' ? '#fee2e2' :
                                         '#fef3c7',
                              color: profile.verification_status === 'verified' ? '#166534' :
                                     profile.verification_status === 'rejected' ? '#991b1b' :
                                     '#92400e'
                            }}>
                              {profile.verification_status}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                            {profile.registration_submitted_at ? new Date(profile.registration_submitted_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => {
                                  setPreviousView('pending-registrations')
                                  setSelectedProfile(profile)
                                  setView('registration-details')
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'var(--blue)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: 600
                                }}
                              >
                                View Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card version */}
                <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                  {getUniquePendingRegistrations().map((profile) => (
                    <div key={profile.id} className="data-table-card">
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Name</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>{profile.full_name || 'N/A'}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Role</span>
                        <span className="data-table-card-value">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {getProfileRoles(profile).map((role) => (
                              <span key={role} style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                background: 'var(--bg)',
                                color: 'var(--muted)'
                              }}>
                                {role.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Email</span>
                        <span className="data-table-card-value">{profile.email}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Phone</span>
                        <span className="data-table-card-value">{profile.business_phone || profile.phone || 'N/A'}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Business</span>
                        <span className="data-table-card-value">{profile.business_name || 'N/A'}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Location</span>
                        <span className="data-table-card-value">{profile.location_city && profile.location_county ? `${profile.location_city}, ${profile.location_county}` : 'N/A'}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Onboarding</span>
                        <span className="data-table-card-value">
                          {profile.onboarding_completed ? (
                            <span style={{ color: 'var(--green)', fontWeight: 600 }}>Completed</span>
                          ) : (
                            <span style={{ color: 'var(--amber)', fontWeight: 600 }}>In Progress</span>
                          )}
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Status</span>
                        <span className="data-table-card-value">
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            background: profile.verification_status === 'verified' ? '#dcfce7' :
                                       profile.verification_status === 'rejected' ? '#fee2e2' :
                                       '#fef3c7',
                            color: profile.verification_status === 'verified' ? '#166534' :
                                   profile.verification_status === 'rejected' ? '#991b1b' :
                                   '#92400e'
                          }}>
                            {profile.verification_status}
                          </span>
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Submitted</span>
                        <span className="data-table-card-value">{profile.registration_submitted_at ? new Date(profile.registration_submitted_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Actions</span>
                        <button
                          onClick={() => {
                            setPreviousView('pending-registrations')
                            setSelectedProfile(profile)
                            setView('registration-details')
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--blue)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
              )}
            </div>
          )}

          {view === 'registration-details' && selectedProfile && (
            <div className="card">
              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  onClick={() => {
                    // If profile is pending, go to pending-registrations
                    // Otherwise, go to the previous view or overview
                    if (selectedProfile.verification_status === 'pending') {
                      setView('pending-registrations')
                    } else if (previousView) {
                      setView(previousView)
                    } else {
                      setView('overview')
                    }
                    // Clear selected profile to prevent modal from opening
                    setSelectedProfile(null)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--blue)',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
                <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Registration Details</h2>
                <p style={{ color: 'var(--muted)', fontSize: '0.95rem' }}>{selectedProfile.email}</p>
              </div>

              {/* Personal Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Personal Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.full_name || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role(s)</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {getProfileRoles(selectedProfile).map((role) => (
                        <span key={role} style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          background: 'var(--bg)',
                          color: 'var(--text)'
                        }}>
                          {role.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.email}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.business_phone || selectedProfile.phone || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Business Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Name</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.business_name || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registration Number</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.business_registration || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax PIN</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.tax_pin || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Address</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.business_address || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Location Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>City</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.location_city || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Area</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.location_area || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>County</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.location_county || 'N/A'}</div>
                  </div>
                </div>
                {selectedProfile.location_link && (
                  <div style={{ marginTop: '1rem', padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Google Maps Link</div>
                    <a href={selectedProfile.location_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.95rem', color: 'var(--blue)', textDecoration: 'none', wordBreak: 'break-all' }}>
                      {selectedProfile.location_link}
                    </a>
                  </div>
                )}
              </div>

              {/* Fleet Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Fleet Information</h3>
                {(() => {
                  const hasValidFleetArray = selectedProfile.vehicle_photos &&
                    Array.isArray(selectedProfile.vehicle_photos) &&
                    selectedProfile.vehicle_photos.length > 0 &&
                    selectedProfile.vehicle_photos[0]?.truck_type

                  if (hasValidFleetArray) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {selectedProfile.vehicle_photos.map((truck: any, index) => (
                          <div key={index} style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
                              <div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Type</div>
                                <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{truck.truck_type || 'N/A'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Plate</div>
                                <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{truck.truck_plate || 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  } else {
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                        <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Type</div>
                          <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.truck_type || 'N/A'}</div>
                        </div>
                        <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Plate</div>
                          <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.truck_plate || 'N/A'}</div>
                        </div>
                      </div>
                    )
                  }
                })()}
              </div>

              {/* Banking Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Banking & Payment</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Name</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.bank_name || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>M-Pesa Number</div>
                    <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 500 }}>{selectedProfile.mpesa_number || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents */}
              {selectedProfile.documents && Object.keys(selectedProfile.documents).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text)' }}>Uploaded Documents</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    {Object.entries(selectedProfile.documents).map(([docName, docUrl]: [string, any]) => (
                      <div key={docName} style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {docName.replace(/_/g, ' ')}
                        </div>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: 'var(--blue)',
                            color: 'white',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <FileText size={16} />
                          View Document
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedProfile.verification_status === 'pending' && (
                <div style={{ display: 'flex', gap: '1rem', paddingTop: '2rem', borderTop: '2px solid var(--border)' }}>
                  <button
                    onClick={() => setShowApproveConfirm(true)}
                    style={{
                      flex: 1,
                      padding: '1rem 2rem',
                      background: 'var(--green)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    Approve Registration
                  </button>
                  <button
                    onClick={() => setShowRejectConfirm(true)}
                    style={{
                      flex: 1,
                      padding: '1rem 2rem',
                      background: 'var(--red)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    Reject Registration
                  </button>
                </div>
              )}

              {selectedProfile.verification_status === 'verified' && (
                <div style={{
                  padding: '1.5rem',
                  background: '#dcfce7',
                  borderRadius: '8px',
                  border: '2px solid #86efac',
                  textAlign: 'center',
                  paddingTop: '2rem',
                  borderTop: '2px solid var(--border)'
                }}>
                  <CheckCircle size={32} color="#16a34a" style={{ marginBottom: '0.5rem' }} />
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.25rem' }}>
                    Registration Approved
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#166534' }}>
                    This registration has been approved and the user can now accept jobs.
                  </div>
                </div>
              )}

              {selectedProfile.verification_status === 'rejected' && (
                <div style={{
                  padding: '1.5rem',
                  background: '#fee2e2',
                  borderRadius: '8px',
                  border: '2px solid #fca5a5',
                  textAlign: 'center',
                  paddingTop: '2rem',
                  borderTop: '2px solid var(--border)'
                }}>
                  <AlertCircle size={32} color="#dc2626" style={{ marginBottom: '0.5rem' }} />
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.25rem' }}>
                    Registration Rejected
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                    This registration has been rejected.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Approve Confirmation Modal */}
          {showApproveConfirm && selectedProfile && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={() => setShowApproveConfirm(false)}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  maxWidth: '500px',
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle size={24} color="#16a34a" />
                    </div>
                    <div>
                      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Approve Registration</h2>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{selectedProfile.full_name}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                    Are you sure you want to approve this registration? This will activate their account and allow them to accept jobs.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => setShowApproveConfirm(false)}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1.5rem',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setProcessing(true)
                        try {
                          const { error } = await supabase.from('profiles').update({
                            verification_status: 'verified',
                            is_online: true,
                            verified_at: new Date().toISOString()
                          }).eq('id', selectedProfile.id)

                          if (error) {
                            alert('Error approving registration: ' + error.message)
                            setProcessing(false)
                            return
                          }

                          setActionSuccess({ type: 'approve' })
                          setShowApproveConfirm(false)
                          setTimeout(() => {
                            setView(previousView)
                            window.location.reload()
                          }, 1500)
                        } catch (err) {
                          alert('Error approving registration: ' + (err as Error).message)
                          setProcessing(false)
                        }
                      }}
                      disabled={processing}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1.5rem',
                        background: processing ? 'var(--muted)' : 'var(--green)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: processing ? 'not-allowed' : 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600
                      }}
                    >
                      {processing ? 'Processing...' : 'Confirm Approval'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reject Confirmation Modal */}
          {showRejectConfirm && selectedProfile && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={() => setShowRejectConfirm(false)}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  maxWidth: '500px',
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AlertCircle size={24} color="#dc2626" />
                    </div>
                    <div>
                      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Reject Registration</h2>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{selectedProfile.full_name}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: '1.6', marginBottom: '1rem' }}>
                    Are you sure you want to reject this registration? Please provide a reason for rejection.
                  </p>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Explain why this registration is being rejected and what needs to be corrected..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => {
                        setShowRejectConfirm(false)
                        setRejectionReason('')
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1.5rem',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!rejectionReason.trim()) {
                          alert('Please provide a rejection reason')
                          return
                        }
                        setProcessing(true)
                        try {
                          const { error } = await supabase.from('profiles').update({
                            verification_status: 'rejected',
                            verification_notes: { rejection_reason: rejectionReason },
                            is_online: false
                          }).eq('id', selectedProfile.id)

                          if (error) {
                            alert('Error rejecting registration: ' + error.message)
                            setProcessing(false)
                            return
                          }

                          setActionSuccess({ type: 'reject' })
                          setShowRejectConfirm(false)
                          setRejectionReason('')
                          setTimeout(() => {
                            setView(previousView)
                            window.location.reload()
                          }, 1500)
                        } catch (err) {
                          alert('Error rejecting registration: ' + (err as Error).message)
                          setProcessing(false)
                        }
                      }}
                      disabled={processing}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1.5rem',
                        background: processing ? 'var(--muted)' : 'var(--red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: processing ? 'not-allowed' : 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600
                      }}
                    >
                      {processing ? 'Processing...' : 'Confirm Rejection'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {actionSuccess.type && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}>
              <div
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  maxWidth: '400px',
                  width: '100%',
                  padding: '2rem',
                  textAlign: 'center',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: actionSuccess.type === 'approve' ? '#dcfce7' : '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  {actionSuccess.type === 'approve' ? (
                    <CheckCircle size={32} color="#16a34a" />
                  ) : (
                    <AlertCircle size={32} color="#dc2626" />
                  )}
                </div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                  {actionSuccess.type === 'approve' ? 'Registration Approved!' : 'Registration Rejected'}
                </h2>
                <p style={{ fontSize: '0.95rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
                  {actionSuccess.type === 'approve'
                    ? 'The registration has been approved and the user can now accept jobs.'
                    : 'The registration has been rejected and the user has been notified.'}
                </p>
              </div>
            </div>
          )}

          {view === 'settings' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Admin Settings</h2>
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked />
                    <span>Enable user registration</span>
                  </label>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked />
                    <span>Require operator verification</span>
                  </label>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked />
                    <span>Enable insurance integration</span>
                  </label>
                </div>
                <button className="btn btn-primary">Save Settings</button>
              </div>
            </div>
          )}

          {/* Profile Details Modal */}
          {selectedProfile && view !== 'registration-details' && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius)',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                    Profile Details
                  </h3>
                  <button
                    onClick={() => setSelectedProfile(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      color: 'var(--muted)'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  {/* Header with role badge */}
                  <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'var(--bg)',
                      border: '2px solid var(--border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: 'var(--red)'
                    }}>
                      {(selectedProfile.full_name || selectedProfile.business_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                        {selectedProfile.full_name || selectedProfile.business_name || 'N/A'}
                      </div>
                      <span className={`badge ${selectedProfile.verification_status === 'verified' ? 'badge-green' : selectedProfile.verification_status === 'pending' ? 'badge-amber' : 'badge-blue'}`} style={{ marginTop: '0.25rem' }}>
                        {selectedProfile.role}
                      </span>
                    </div>
                  </div>

                  {/* Info Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <Phone size={16} style={{ color: 'var(--green)' }} />
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Phone</div>
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>{selectedProfile.business_phone || selectedProfile.phone || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <Mail size={16} style={{ color: 'var(--blue)' }} />
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Email</div>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{selectedProfile.email || 'N/A'}</div>
                    </div>
                  </div>

                  {selectedProfile.business_address && (
                    <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <MapPin size={16} style={{ color: 'var(--red)' }} />
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Address</div>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{selectedProfile.business_address}</div>
                    </div>
                  )}

                  {selectedProfile.truck_type && selectedProfile.truck_plate && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Truck size={16} style={{ color: 'var(--blue)' }} />
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Truck Type</div>
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>{selectedProfile.truck_type}</div>
                      </div>
                      <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <CreditCard size={16} style={{ color: 'var(--purple)' }} />
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Truck Plate</div>
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>{selectedProfile.truck_plate}</div>
                      </div>
                    </div>
                  )}

                  {selectedProfile.verification_status && (
                    <div style={{ padding: '1.25rem', background: selectedProfile.verification_status === 'verified' ? '#dcfce7' : selectedProfile.verification_status === 'pending' ? '#fef3c7' : '#fee2e2', borderRadius: 'var(--radius)', border: `2px solid ${selectedProfile.verification_status === 'verified' ? '#86efac' : selectedProfile.verification_status === 'pending' ? '#fcd34d' : '#fca5a5'}`, marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <CheckCircle size={18} style={{ color: selectedProfile.verification_status === 'verified' ? '#166534' : selectedProfile.verification_status === 'pending' ? '#92400e' : '#991b1b' }} />
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: selectedProfile.verification_status === 'verified' ? '#166534' : selectedProfile.verification_status === 'pending' ? '#92400e' : '#991b1b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verification Status</div>
                      </div>
                      <span className={`badge ${selectedProfile.verification_status === 'verified' ? 'badge-green' : selectedProfile.verification_status === 'pending' ? 'badge-amber' : 'badge-red'}`} style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}>
                        {selectedProfile.verification_status}
                      </span>
                    </div>
                  )}

                  <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <Clock size={16} style={{ color: 'var(--muted)' }} />
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)' }}>Registered</div>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{new Date(selectedProfile.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
